import { API } from '@aws-amplify/api';
import dayjs from '@common/@types/dayjs-custom';
import LZString from 'lz-string';

// Used for both getting dates in a schoolYear/session/week combo
// and for getting schoolYear/session/week associated with a specific
// date

export interface GetAllDaysInput {
  schoolYear?: string;
  session?: number;
  week?: number;
  date?: string;
}

export interface GetAllDaysOutput {
  day_of: string;
  school_year: string;
  session: number;
  week_num: number;
  is_weekday: boolean;
  is_holiday: boolean;
  percent_through_session: number;
}

export interface GetAllDaysOutputWeekInfo {
  [key: string]: GetAllDaysOutputSchoolYear;
}
export interface GetAllDaysOutputSchoolYear {
  [key: string]: GetAllDaysOutputSession;
}

export interface GetAllDaysOutputSession {
  [key: string]: GetAllDaysOutput[];
}

export interface GetAllDaysOutputDayInfo {
  [key: string]: GetAllDaysOutput;
}

export interface GetAllDaysFnOutput {
  dayInfo: GetAllDaysOutputDayInfo;
  weekInfo: GetAllDaysOutputWeekInfo;
}

// How long an item in the cache should stay before being invalidated
const CACHE_TTL = 1000 * 60 * 60; // in ms

// LocalStorage key
const LOCAL_STORAGE_KEY = 'dash-date-cache';

let loadingPromise: null | Promise<void> = null;

interface DateCacheType extends GetAllDaysFnOutput {
  expiry: number;
}

let dateCache: DateCacheType = {
  dayInfo: {},
  weekInfo: {
    /*
  school_year: {
      session: {
          week_num: [day1, day2, ...]
      }
  }
  */
  },
  expiry: 0,
};

// Gets YY-YY formatted latest year
export async function getLatestYear() {
  if (!loadingPromise) getAllDays();
  await loadingPromise;

  // Lookup from int version to actual key
  const lookup: { [key: number]: string } = {};
  for (const key in dateCache.weekInfo) {
    lookup[parseInt(key)] = key;
  }

  // Parse "22-23" as ints for the first years
  const yearsAsInts = Object.keys(lookup)
    .map((a) => parseInt(a))
    .sort();
  // Get the original string version
  return lookup[Math.max.apply(null, yearsAsInts)];
}

// Gets YYYY-YYYY formatted last year
export async function getLatestYearFull() {
  const latestYear = await getLatestYear();

  // Get the full year for the first date in this school year
  const yearCache = dateCache.weekInfo[latestYear];
  const sessionCache = yearCache[Object.keys(yearCache)[0]];
  const weekCache = sessionCache[Object.keys(sessionCache)[0]];
  const fullYear = weekCache[0].day_of.split('-')[0]; // YYYY-MM-DD formatted

  // Expand that into "[year]-[year + 1]"
  return `${fullYear}-${parseInt(fullYear) + 1}`;
}

export async function getAllYears() {
  if (!loadingPromise) getAllDays();
  await loadingPromise;

  // Lookup from int version to actual key
  const lookup: { [key: number]: string } = {};
  for (const key in dateCache.weekInfo) {
    lookup[parseInt(key)] = key;
  }

  // Parse "22-23" as ints for the first years
  const yearsAsInts = Object.keys(lookup)
    .map((a) => parseInt(a))
    .sort();
  // Get the original string version
  return yearsAsInts.map((a) => lookup[a]);
}

export async function getAllSessions(schoolYear?: string) {
  if (!loadingPromise) getAllDays();
  await loadingPromise;

  const sessions = new Set<string>();
  for (const year in dateCache.weekInfo) {
    if (schoolYear && year !== schoolYear) continue;

    for (const session in dateCache.weekInfo[year]) {
      sessions.add(session);
    }
  }

  return Array.from(sessions).sort();
}

function emptyDateCache() {
  dateCache = {
    dayInfo: {},
    weekInfo: {},
    expiry: 0,
  };
}

function initCache() {
  if (dateCache && Object.keys(dateCache.dayInfo).length > 0) return;

  const cacheRaw = localStorage?.getItem(LOCAL_STORAGE_KEY);

  // Safety: in case we still have JSON, just parse it
  if (cacheRaw?.[0] === '{') {
    try {
      dateCache = JSON.parse(cacheRaw);
    } catch (error) {
      console.error('Failed to parse getAllDays cache from local storage:', error);
      emptyDateCache();
    }
  }
  // Otherwise, assume it's LZString-encoded and decompress it
  else if (cacheRaw) {
    try {
      dateCache = JSON.parse(LZString.decompress(cacheRaw));
      // Notably: first half handles `null` values somehow being there, latter half handles strings/numbers/etc.
      if (!dateCache || typeof dateCache !== 'object') {
        throw new Error('Decompression failed - manual catch');
      }
    } catch (error) {
      emptyDateCache();
      writeCache();
      console.warn(
        'Cache did not start with `{` and JSON parsing failed; something went wrong. Defaulting to empty cache; original error:'
      );
      console.warn(error);
    }
  } else emptyDateCache();
}

async function load(input: GetAllDaysInput = {}) {
  const apiName = process.env.NEXT_PUBLIC_DASH_API_NAME!;
  const path = '/all-days/day';
  const myInit = {
    headers: {},
    queryStringParameters: input,
  };

  if (typeof window !== 'undefined') {
    initCache();
  }

  if (dateCache.expiry > dayjs().valueOf()) {
    return;
  }

  const response = await API.get(apiName, path, myInit);
  const data = response.result.data.json;
  // Quick-and-dirty reformat for caching
  const {
    dayInfo,
    weekInfo,
  }: { dayInfo: GetAllDaysOutputDayInfo; weekInfo: GetAllDaysOutputWeekInfo } = dateCache;
  for (const day of data) {
    const dateString: string = day.day_of.split('T')[0];
    dayInfo[dateString] = day;

    if (!(day.school_year in weekInfo)) weekInfo[day.school_year] = {};
    const yearInfo: GetAllDaysOutputSchoolYear = weekInfo[day.school_year];
    if (!(day.session in yearInfo)) yearInfo[day.session] = {};
    const sessionInfo: GetAllDaysOutputSession = yearInfo[day.session];
    if (!(day.week_num in sessionInfo)) sessionInfo[day.week_num] = <GetAllDaysOutput[]>[];
    sessionInfo[day.week_num].push(day);
  }
  dateCache.expiry = dayjs().valueOf() + CACHE_TTL;
  if (typeof window !== 'undefined') {
    try {
      writeCache();
    } catch (error) {
      console.error('Failed to write getAllDays cache to local storage:', error);
    }
  }
}

export function clearDateCache() {
  dateCache.dayInfo = {};
  dateCache.weekInfo = {};
  dateCache.expiry = 0;
  loadingPromise = null;
  writeCache();
}

function writeCache() {
  let toWrite = JSON.stringify(dateCache);
  toWrite = LZString.compress(toWrite);
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, toWrite);
  } catch (error) {
    console.error('Failed to write getAllDays cache to local storage:', error);
  }
}

export function getDateCache() {
  return dateCache;
}

export async function getAllDays(input: GetAllDaysInput = {}): Promise<GetAllDaysFnOutput> {
  try {
    if (loadingPromise) {
      await loadingPromise;
      return dateCache;
    }

    loadingPromise = load(input);
    await loadingPromise;

    return dateCache;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
